import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import {
  SAPSection,
  OomPahPlayer,
  SAPChordRow,
  SAPCallout,
  SAPQuote,
  SAPPracticeLadder,
} from "@/components/sap/SAPShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { NoteImage, NoteImagePair } from "@/components/mt/NoteImage";

export default function SingAndPlay7() {
  const { lesson, course, accent } = findLesson("sing-and-play", "7");
  return (
    <LessonShell
      course={course}
      courseSlug="sing-and-play"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >

      <AnimatedExplainer
        src="/animations/sing-and-play/SP7-Gospel-Folk-Animated.mp4"
        caption={`Gospel-folk strum — the 16th-note ghost-strum lift`}
        hint="≈ 24 sec · gospel sway"
      />
      {/* ── 1. Big idea ─────────────────────────────────────────────────── */}
      <SAPSection eyebrow="Lesson 7 · Gospel Folk" title="Lord I Lift Your Name On High — Thinking Like a Guitarist">
        <p>
          Written by Rick Founds in 1989 and recorded for Maranatha! Music, this song has
          been sung in English-speaking churches for over thirty years. It's simple on
          the surface: G–C–D–Em, the textbook I–IV–V–vi in G major. But the magic isn't
          in the chords — it's in the <em>rhythm</em>.
        </p>
        <p>
          This is the lesson where you stop thinking like a pianist and start thinking like
          an <strong>acoustic guitarist</strong>. A guitarist strums, percusses, ghosts,
          and bounces — creating a rhythmic feel that the piano almost never produces by
          default. Jason's technique converts the piano into that strumming instrument.
        </p>
        <SAPCallout label="The big shift in this lesson" variant="insight">
          You are not going to "play the piano." You are going to make the piano sound
          like a Martin D-28 acoustic guitar. Every technique in this lesson — ghost
          strums, thumb fills, percussive brush-backs — serves that one goal.
        </SAPCallout>
      </SAPSection>

      {/* ── 2. Reference video ──────────────────────────────────────────── */}
      <YouTubeEmbed
        videoId="tQiapzfQoq0"
        title="Reference · Lord I Lift Your Name On High (Classic Worship Recording)"
        caption="Listen to the acoustic guitar — the light strum pattern, the ghost notes between chords. Your piano is going to replicate that feel."
      />

      {/* ── 3. The chords ───────────────────────────────────────────────── */}
      <SAPSection eyebrow="The Harmony" title="G Major: I–IV–V–vi and Back Again">
        <p>
          The verse moves through G–C–D–Em; the chorus reverses to G–D–C–G. Both
          shapes use the exact same four chords — same family, different order. Hearing
          them in both directions trains your ear to recognise chord function, not just
          chord sequence.
        </p>

        <RomanNumeralAnalysis
          label="Verse — I IV V vi"
          caption="The verse sits in the tonic-ward direction: I to IV to V, then the vi gives you the wistful turn before the chorus lifts."
          chords={[
            { chord: "G",  rn: "I",  function: "Tonic",        pitches: ["G3","B3","D4","G4"] },
            { chord: "C",  rn: "IV", function: "Pre-dominant", pitches: ["C3","E3","G3","C4"] },
            { chord: "D",  rn: "V",  function: "Dominant",     pitches: ["D3","F#3","A3","D4"] },
            { chord: "Em", rn: "vi", function: "Submediant",      pitches: ["E3","G3","B3","E4"] },
          ]}
        />

        <RomanNumeralAnalysis
          label="Chorus — I V IV I"
          caption="The chorus reverses the direction. Starting on I, stepping up to V, landing on IV (the open, lifted sound), and coming home to I. This arc feels like an arm being raised - which is exactly the lyric."
          chords={[
            { chord: "G",  rn: "I",  function: "Tonic",        pitches: ["G3","B3","D4","G4"] },
            { chord: "D",  rn: "V",  function: "Dominant",     pitches: ["D3","F#3","A3","D4"] },
            { chord: "C",  rn: "IV", function: "Pre-dominant", pitches: ["C3","E3","G3","C4"] },
            { chord: "G",  rn: "I",  function: "Tonic",        pitches: ["G3","B3","D4","G4"] },
          ]}
        />

        <SAPChordRow
          label="All four chords — click to hear"
          chords={[
            { label: "G",  rn: "I",  pitches: ["G3","B3","D4","G4"],  fn: "tonic"       },
            { label: "C",  rn: "IV", pitches: ["C3","E3","G3","C4"],  fn: "subdominant" },
            { label: "D",  rn: "V",  pitches: ["D3","F#3","A3","D4"], fn: "dominant"    },
            { label: "Em", rn: "vi", pitches: ["E3","G3","B3","E4"],  fn: "submediant"     },
          ]}
          caption="G and Em share the B and D notes — that's why the progression moves so naturally between them. They're cousins."
        />
      </SAPSection>

      {/* ── 4. Folk-gospel rhythm ────────────────────────────────────────── */}
      <SAPSection eyebrow="Technique · The Folk-Gospel Rhythm" title="Three to a Beat: The Guitar Strum at the Piano">
        <p>
          The folk-gospel strum divides each beat into <strong>three</strong>, not two —
          the same lilt as a shuffle. Counted aloud:
          <strong>"1 — and-a, 2 — and-a, 3 — and-a, 4 — and-a."</strong> That "and-a" is
          the giveaway: two evenly spaced brush-backs, not one off-beat. The first hit is
          the downstroke and takes the accent; the other two are the hand brushing back.
        </p>
        <p>
          At the piano, translate this as:
        </p>
        <ul className="ml-5 list-disc space-y-1">
          <li><strong>On the beat:</strong> LH root octave — full, resonant, like a bass string plucked hard. Accented, but the same length as the other two.</li>
          <li><strong>"And" (second third of the beat):</strong> RH fifth chord — bright, clear, like the strum hitting the mid strings.</li>
          <li><strong>"A" (final third):</strong> Both thumbs, ghost note — barely audible, like the hand brushing back.</li>
        </ul>

        <SAPCallout label="The ghost strum gesture" variant="tip">
          After the RH chord hits, <em>lift your fingers but keep the thumb down</em>,
          then brush the thumb back across the keys. No sound — or barely any. The gesture
          is physical, not acoustic. Your hand needs to feel the strum even when the piano
          doesn't hear it. That physical memory is what creates the rhythmic groove.
        </SAPCallout>

        <SAPQuote>
          Now you can't do the ghost notes of the guitar very well, the percussive
          stuff, but you can try. That's what we're going to do here.
        </SAPQuote>
      </SAPSection>

      {/* ── 5. OomPah player ─────────────────────────────────────────────── */}
      <SAPSection eyebrow="Interactive · Play Along" title="The Folk-Gospel Loop in G">
        <p>
          Press play and listen for the LH bass locking into each beat while the RH chord
          bounces on the off-beats. At 95 bpm this sits right in the groove of the original
          recording.
        </p>
        <OomPahPlayer
          progression={[
            { label: "G",  bars: 2, lhRoot: "G2",  lhFifth: "D3",  rh: ["G4","D5","G5"] },
            { label: "C",  bars: 2, lhRoot: "C3",  lhFifth: "G3",  rh: ["C4","G4","C5"] },
            { label: "D",  bars: 2, lhRoot: "D3",  lhFifth: "A3",  rh: ["D4","A4","D5"] },
            { label: "Em", bars: 2, lhRoot: "E3",  lhFifth: "B3",  rh: ["E4","B4","E5"] },
          ]}
          bpm={95}
          label="LORD I LIFT — G major folk-gospel loop"
        />
      </SAPSection>

      {/* ── 6. Thumb fills & bass motion ────────────────────────────────── */}
      <SAPSection eyebrow="Going Deeper" title="The Dual-Thumb Fill & Bass Anchoring">
        <p>
          Once the basic pattern is solid, Jason adds a second layer: the{" "}
          <strong>dual-thumb fill</strong>. When the LH plays the root octave on beat 1,
          the RH thumb simultaneously plays the fifth of the chord — high up, soft,
          like a guitar harmonic. This creates a three-voice texture from just the
          thumbs and the chord.
        </p>
        <p>
          The left hand becomes "the bottom of the boat" — it never moves from the root,
          except to occasionally walk up or down a step for colour. Bass consistency is
          what lets the RH feel free to do anything.
        </p>



        <SAPCallout label="Chorus vs verse texture" variant="insight">
          For the verse, play softer fifth chords in the RH — just root + fifth, open
          and airy. For the chorus ("Lord I lift Your name on high"), add the third to
          make a full triad and <em>increase velocity</em> — the dynamic swell is the
          musical "lift" the lyric is describing. Let the harmony do the emotional work.
        </SAPCallout>
      </SAPSection>

      {/* ── 7. Practice ladder ──────────────────────────────────────────── */}
      <SAPSection eyebrow="Your Practice Plan" title="35-Minute Folk-Gospel Workout">
        <SAPPracticeLadder
          intro="This song is a rhythm lesson wearing a chord lesson's clothes. Prioritise the feel of the pattern above everything else."
          rungs={[
            {
              title: "Strum the air first",
              minutes: 3,
              detail: "Stand up, strum an imaginary guitar to the recording. Feel quarter + two eighths in your arm. Then sit at the piano and reproduce that arm motion with your fingers.",
              accent: "amber",
            },
            {
              title: "LH roots — octaves only",
              minutes: 5,
              detail: "Play G–C–D–Em as bass octaves on beat 1 only. Silence between changes. Lock the hand shapes.",
              accent: "purple",
            },
            {
              title: "Add RH fifth chord hits",
              minutes: 5,
              detail: "LH root on beat 1. RH fifth chord on the &-of-1. Two voices, two beats. Build the interlock.",
              accent: "cyan",
            },
            {
              title: "Add ghost strum gesture",
              minutes: 8,
              detail: "After the RH chord hit, lift and brush back. No sound needed — just the physical motion. Your hand will start to feel the groove.",
              accent: "emerald",
            },
            {
              title: "Full pattern + chord changes",
              minutes: 7,
              detail: "Put all three elements together through the full verse progression G–C–D–Em and the chorus G–D–C–G.",
              accent: "rose",
            },
            {
              title: "Sing and play",
              minutes: 7,
              detail: "\"Lord I lift Your name on high.\" Start the pattern, then bring in your voice. Don't pause the pattern when you start singing — let it run underneath.",
              accent: "purple",
            },
          ]}
        />
        <SAPQuote>
          The first thing for folk music is get a fifth chord running in the right hand.
          You can either play that fifth chord with root position or with the inversion.
        </SAPQuote>
      </SAPSection>


      {/* ── Jason's notes ────────────────────────────────────────────── */}
      <SAPSection eyebrow="Jason's chord chart" title="Handwritten teaching notes">
        <p>
          This is Jason's original handwritten chord chart for this song — the same one he
          uses in the video lesson. Click to zoom in and follow along.
        </p>
        <NoteImage
          src="/notes/sing-and-play/07-lord-i-lift.png"
          caption="Jason's chord chart — Lord I Lift Your Name on High in G major"
        />
      </SAPSection>
    </LessonShell>
  );
}
